import { Modal } from '@/components/ui/Modal';
import { describeHttpStatus, STATUS_CLASSES } from '@/config/httpStatus';
import { formatInterval, formatNumber, formatPercent } from '@/utils/format';
import type { HttpStatsPoint, HttpStatsTimeseries, StatusClassCounts } from '@/types/stats';
import { StatsFreshness } from './StatsFreshness';
import { StatsTotals } from './StatsTotals';
import { StatusChip } from './StatusChip';
import { StatusTimeChart } from './StatusTimeChart';
import './stats.css';

export interface DomainStatsModalProps {
  /** Domain to show; null closes the modal */
  domain: string | null;
  /** e.g. "last 24 hours" */
  rangeDescription: string;
  /**
   * Chart the requests over time; otherwise (a single-bin range such as 1m)
   * show the requests by status class
   */
  showTimeChart: boolean;
  timeseries: HttpStatsTimeseries | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
}

/**
 * Requests per status class over all buckets
 */
function sumStatusClasses(points: HttpStatsPoint[]): StatusClassCounts {
  const totals: StatusClassCounts = { '1xx': 0, '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0 };
  for (const point of points) {
    for (const cls of STATUS_CLASSES) totals[cls] += point.status_classes[cls];
  }
  return totals;
}

/**
 * Drill-down of one site: requests over time by status class (or, for a
 * single-minute range, the status class totals) and every status code seen
 * in the range with its share and meaning
 */
export function DomainStatsModal({
  domain,
  rangeDescription,
  showTimeChart,
  timeseries,
  isLoading,
  error,
  onClose,
}: DomainStatsModalProps) {
  const totalRequests = timeseries?.points.reduce((sum, point) => sum + point.requests, 0) ?? 0;

  return (
    <Modal isOpen={domain !== null} onClose={onClose} title={domain} size="xl" className="domain-stats-modal">
      {error ? (
        <div className="domain-stats-error" role="alert">
          {error}
        </div>
      ) : isLoading || !timeseries ? (
        <p className="domain-stats-loading">Loading statistics…</p>
      ) : (
        <div className="domain-stats">
          <div className="domain-stats-header">
            <p className="domain-stats-summary">
              {formatNumber(totalRequests)} requests in the {rangeDescription}
            </p>
            <StatsFreshness dataUntil={timeseries.data_until} />
          </div>

          {showTimeChart ? (
            <StatusTimeChart
              points={timeseries.points}
              intervalSeconds={timeseries.interval_seconds}
              title={`Requests per ${formatInterval(timeseries.interval_seconds)} by status class`}
            />
          ) : (
            <StatsTotals requests={totalRequests} statusClasses={sumStatusClasses(timeseries.points)} />
          )}

          <h3 className="domain-stats-heading">Status codes</h3>
          {timeseries.statuses.length === 0 ? (
            <p className="domain-stats-empty">No requests in this period.</p>
          ) : (
            <table className="domain-stats-table">
              <thead>
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col">Meaning</th>
                  <th scope="col" className="domain-stats-number">
                    Requests
                  </th>
                  <th scope="col" className="domain-stats-number">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody>
                {timeseries.statuses.map((item) => (
                  <tr key={item.status} data-testid="domain-status-row">
                    <td>
                      <StatusChip status={item.status} />
                    </td>
                    <td>{describeHttpStatus(item.status)}</td>
                    <td className="domain-stats-number">{formatNumber(item.count)}</td>
                    <td className="domain-stats-number">{formatPercent(item.share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Modal>
  );
}
